import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, deleteDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../utils/authContext";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import { Search, Plus, Edit, Trash2, Database, Settings, HelpCircle } from "lucide-react";
import ModalPortal from "../utils/ModalPortal";

const COLLECTION_NAME = "documentFieldSchemas";

// Default schema for table column fields - defines editable properties
const DEFAULT_SCHEMA = {
  name: { type: "string", required: true },
  type: { type: "string", required: false },
  width: { type: "number", required: false }
};

const AdminFieldsEditor = () => {
  const { currentUser } = useAuth();
  const [definitions, setDefinitions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  
  // Selected definition for drill-down
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  
  // Dialog states
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [definitionToDelete, setDefinitionToDelete] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  useEffect(() => {
    fetchDefinitions();
  }, []);

  const fetchDefinitions = async () => {
    try {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      const data = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setDefinitions(data);
    } catch (error) {
      console.error("Error fetching document fields:", error);
    }
    setIsLoading(false);
  };

  const handleNewDefinition = () => {
    setEditingDefinition(null);
    setSettingsDialogOpen(true);
  };

  const handleOpenSettings = (definition) => {
    setEditingDefinition(definition);
    setSettingsDialogOpen(true);
  };

  const handleEditFields = (definition) => {
    setSelectedDefinition(definition);
  };

  const handleDeleteDefinition = (definition) => {
    setDefinitionToDelete(definition);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!definitionToDelete) return;
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, definitionToDelete.id));
      setDeleteDialogOpen(false);
      setDefinitionToDelete(null);
      fetchDefinitions();
    } catch (error) {
      console.error("Error deleting field definition:", error);
    }
  };

  const handleBackToList = () => {
    setSelectedDefinition(null);
    fetchDefinitions(); // Refresh to get updated counts
  };

  const getFieldCount = (definition) => {
    if (!definition || !definition.fields) return 0;
    return Object.keys(definition.fields).length;
  };

  const filteredDefinitions = definitions.filter(def => {
    if (categoryFilter !== "all" && def.category !== categoryFilter) {
      return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      def.id?.toLowerCase().includes(query) ||
      def.displayName?.toLowerCase().includes(query)
    );
  });

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
      return date.toLocaleDateString("en-US", { 
        month: "short", 
        day: "numeric", 
        year: "numeric" 
      });
    } catch {
      return "";
    }
  };

  // If a definition is selected, show the fields editor
  if (selectedDefinition) {
    return (
      <FieldsEditor
        definition={selectedDefinition}
        onBack={handleBackToList}
        currentUser={currentUser}
        onUpdate={(updatedDef) => setSelectedDefinition(updatedDef)}
      />
    );
  }

  return (
    <div style={{ marginBottom: '60px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
            <Tooltip title="Search field definitions" arrow>
              <Search className="header-search-icon" size={18} />
            </Tooltip>
            <input
              type="text"
              placeholder="Search Definitions"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="header-search-input"
            />
          </div>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="fieldGroup">Field Groups</MenuItem>
              <MenuItem value="tableColumns">Table Columns</MenuItem>
            </Select>
          </FormControl>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Documentation" arrow>
            <IconButton onClick={() => setHelpDialogOpen(true)} size="small">
              <HelpCircle size={20} />
            </IconButton>
          </Tooltip>
          {/* Import JSON button hidden - feature still available if needed
          <Button
            onClick={() => setImportDialogOpen(true)}
            variant="outlined"
          >
            Import JSON
          </Button>
          */}
          <Button
            startIcon={<Plus size={18} />}
            onClick={handleNewDefinition}
            variant="contained"
            color="primary"
          >
            Add Definition
          </Button>
        </div>
      </div>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Display Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Fields</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Updated</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDefinitions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  {isLoading ? (
                    "Loading..."
                  ) : searchQuery || categoryFilter !== "all" ? (
                    "No field definitions match your filters"
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <Database size={48} color="var(--color-text-secondary)" />
                      <span>No field definitions found. Add your first field definition to get started.</span>
                      <Button
                        startIcon={<Plus size={16} />}
                        onClick={handleNewDefinition}
                        variant="outlined"
                        size="small"
                      >
                        Add First Definition
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredDefinitions.map((def) => (
                <TableRow 
                  key={def.id}
                  sx={{ 
                    '&:hover': { backgroundColor: 'var(--color-background-alt)' },
                    cursor: 'pointer'
                  }}
                  onClick={() => handleEditFields(def)}
                >
                  <TableCell>
                    <span style={{ fontFamily: 'monospace' }}>
                      {def.id}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="table-primary-text">
                      {def.displayName}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={def.category === "tableColumns" ? "Table Columns" : "Field Group"}
                      size="small"
                      color={def.category === "tableColumns" ? "secondary" : "primary"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <span className="patient-details-small">
                      {getFieldCount(def)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="patient-details-small">
                      {formatDate(def.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Edit fields" arrow>
                      <IconButton
                        onClick={() => handleEditFields(def)}
                        size="small"
                        color="primary"
                      >
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Definition settings" arrow>
                      <IconButton
                        onClick={() => handleOpenSettings(def)}
                        size="small"
                      >
                        <Settings size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        onClick={() => handleDeleteDefinition(def)}
                        size="small"
                        color="error"
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Settings Dialog (metadata + schema) */}
      <ModalPortal isOpen={settingsDialogOpen}>
        <SettingsDialog
          open={settingsDialogOpen}
          onClose={() => setSettingsDialogOpen(false)}
          editingDefinition={editingDefinition}
          currentUser={currentUser}
          onSave={() => {
            setSettingsDialogOpen(false);
            fetchDefinitions();
          }}
        />
      </ModalPortal>

      {/* Delete Confirmation Dialog */}
      <ModalPortal isOpen={deleteDialogOpen}>
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
          <DialogTitle>Delete Field Definition</DialogTitle>
          <DialogContent>
            Are you sure you want to delete "{definitionToDelete?.displayName || definitionToDelete?.id}"? 
            This will remove all {getFieldCount(definitionToDelete)} fields. This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Import Dialog */}
      <ModalPortal isOpen={importDialogOpen}>
        <ImportDefinitionsDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          currentUser={currentUser}
          onImport={() => {
            setImportDialogOpen(false);
            fetchDefinitions();
          }}
        />
      </ModalPortal>

      {/* Help Dialog */}
      <ModalPortal isOpen={helpDialogOpen}>
        <HelpDialog
          open={helpDialogOpen}
          onClose={() => setHelpDialogOpen(false)}
        />
      </ModalPortal>
    </div>
  );
};

// Settings dialog for creating/editing definition metadata and schema
const SettingsDialog = ({ open, onClose, editingDefinition, currentUser, onSave }) => {
  const [id, setId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [category, setCategory] = useState("fieldGroup");
  const [schemaText, setSchemaText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (editingDefinition) {
      setId(editingDefinition.id || "");
      setDisplayName(editingDefinition.displayName || "");
      setCategory(editingDefinition.category || "fieldGroup");
      setSchemaText(JSON.stringify(editingDefinition.schema || DEFAULT_SCHEMA, null, 2));
    } else {
      setId("");
      setDisplayName("");
      setCategory("fieldGroup");
      setSchemaText(JSON.stringify(DEFAULT_SCHEMA, null, 2));
    }
    setError("");
  }, [editingDefinition, open]);

  const handleSave = async () => {
    setError("");
    
    if (!id.trim() || !displayName.trim()) {
      setError("ID and display name are required");
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(id.trim())) {
      setError("ID must start with a letter and contain only letters, numbers, and underscores");
      return;
    }

    let schema;
    try {
      schema = JSON.parse(schemaText);
      if (typeof schema !== 'object' || Array.isArray(schema)) {
        setError("Schema must be a JSON object");
        return;
      }
    } catch (e) {
      setError("Invalid JSON in schema");
      return;
    }

    setSaving(true);
    try {
      const data = {
        displayName: displayName.trim(),
        category,
        schema,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      };

      if (editingDefinition) {
        await updateDoc(doc(db, COLLECTION_NAME, editingDefinition.id), data);
      } else {
        data.fields = {};
        data.createdAt = serverTimestamp();
        await setDoc(doc(db, COLLECTION_NAME, id.trim()), data);
      }
      onSave();
    } catch (error) {
      console.error("Error saving definition:", error);
      setError("Failed to save: " + error.message);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Settings size={20} />
          {editingDefinition ? "Definition Settings" : "New Field Definition"}
        </Box>
      </DialogTitle>
      <DialogContent>
        {editingDefinition && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Changing the schema may affect how existing fields are displayed. The ID cannot be changed.
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="ID"
            value={id}
            onChange={(e) => setId(e.target.value)}
            fullWidth
            placeholder="e.g., mentalStatusFields"
            helperText="Unique identifier used in code references"
            disabled={!!editingDefinition}
          />
          <TextField
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            fullWidth
            placeholder="e.g., Mental Status Fields"
          />
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={category}
              label="Category"
              onChange={(e) => setCategory(e.target.value)}
              MenuProps={{ sx: { zIndex: 10001 } }}
            >
              <MenuItem value="fieldGroup">Field Group</MenuItem>
              <MenuItem value="tableColumns">Table Columns</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Schema (JSON)"
            value={schemaText}
            onChange={(e) => setSchemaText(e.target.value)}
            fullWidth
            multiline
            rows={8}
            helperText="Define what properties each field has (type: string, number, boolean; required: true/false)"
            sx={{ 
              '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 'var(--text-sm)' }
            }}
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Fields editor component (Level 2 - row-based editing)
const FieldsEditor = ({ definition, onBack, currentUser, onUpdate }) => {
  const [fields, setFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [fieldDialogOpen, setFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);

  // Get schema from definition, fallback to default
  const schema = definition.schema || DEFAULT_SCHEMA;
  const schemaKeys = Object.keys(schema);

  useEffect(() => {
    // Convert object to array
    const fieldsObj = definition.fields || {};
    const fieldsArray = Object.entries(fieldsObj).map(([key, data]) => ({
      id: key,
      ...data
    }));
    setFields(fieldsArray);
  }, [definition]);

  const handleNewField = () => {
    setEditingField(null);
    setFieldDialogOpen(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    setFieldDialogOpen(true);
  };

  const handleDeleteField = (field) => {
    setFieldToDelete(field);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteField = async () => {
    if (!fieldToDelete) return;
    setIsLoading(true);
    try {
      // Remove field from object and save
      const updatedFields = { ...definition.fields };
      delete updatedFields[fieldToDelete.id];
      
      await updateDoc(doc(db, COLLECTION_NAME, definition.id), {
        fields: updatedFields,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      });
      
      // Update local state
      setFields(fields.filter(f => f.id !== fieldToDelete.id));
      onUpdate({ ...definition, fields: updatedFields });
      setDeleteDialogOpen(false);
      setFieldToDelete(null);
    } catch (error) {
      console.error("Error deleting field:", error);
    }
    setIsLoading(false);
  };

  const handleFieldSave = async (fieldData, isNew) => {
    setIsLoading(true);
    try {
      const { id: fieldId, ...fieldValues } = fieldData;
      const updatedFields = { ...definition.fields, [fieldId]: fieldValues };
      
      await updateDoc(doc(db, COLLECTION_NAME, definition.id), {
        fields: updatedFields,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      });
      
      // Update local state
      const fieldsArray = Object.entries(updatedFields).map(([key, data]) => ({
        id: key,
        ...data
      }));
      setFields(fieldsArray);
      onUpdate({ ...definition, fields: updatedFields });
      setFieldDialogOpen(false);
    } catch (error) {
      console.error("Error saving field:", error);
    }
    setIsLoading(false);
  };

  const filteredFields = fields.filter(field => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      field.id?.toLowerCase().includes(query) ||
      schemaKeys.some(key => String(field[key] || "").toLowerCase().includes(query))
    );
  });

  return (
    <div style={{ marginBottom: '60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <Button onClick={onBack} variant="text" size="small">
          ← Back
        </Button>
        <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>{definition.displayName}</span>
        <span style={{ color: 'var(--color-text-secondary)' }}>({fields.length} fields)</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
          <Tooltip title="Search fields" arrow>
            <Search className="header-search-icon" size={18} />
          </Tooltip>
          <input
            type="text"
            placeholder="Search Fields"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="header-search-input"
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            onClick={() => setImportDialogOpen(true)}
            variant="outlined"
          >
            Import JSON
          </Button>
          <Button
            startIcon={<Plus size={18} />}
            onClick={handleNewField}
            variant="contained"
            color="primary"
          >
            Add Field
          </Button>
        </div>
      </div>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
              {schemaKeys.map(key => (
                <TableCell key={key} sx={{ fontWeight: 600 }}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFields.length === 0 ? (
              <TableRow>
                <TableCell colSpan={schemaKeys.length + 2} align="center" sx={{ py: 4 }}>
                  {searchQuery ? "No fields match your search" : "No fields yet. Add your first field to get started."}
                </TableCell>
              </TableRow>
            ) : (
              filteredFields.map((field) => (
                <TableRow 
                  key={field.id}
                  sx={{ '&:hover': { backgroundColor: 'var(--color-background-alt)' } }}
                >
                  <TableCell>
                    <span style={{ fontFamily: 'monospace' }}>{field.id}</span>
                  </TableCell>
                  {schemaKeys.map(key => (
                    <TableCell key={key}>
                      {typeof field[key] === 'boolean' 
                        ? (field[key] ? 'Yes' : 'No')
                        : String(field[key] || "")}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Tooltip title="Edit" arrow>
                      <IconButton onClick={() => handleEditField(field)} size="small" color="primary">
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete" arrow>
                      <IconButton onClick={() => handleDeleteField(field)} size="small" color="error">
                        <Trash2 size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Field Create/Edit Dialog */}
      <ModalPortal isOpen={fieldDialogOpen}>
        <FieldDialog
          open={fieldDialogOpen}
          onClose={() => setFieldDialogOpen(false)}
          schema={schema}
          editingField={editingField}
          existingKeys={fields.map(f => f.id)}
          onSave={handleFieldSave}
        />
      </ModalPortal>

      {/* Delete Field Confirmation */}
      <ModalPortal isOpen={deleteDialogOpen}>
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
          <DialogTitle>Delete Field</DialogTitle>
          <DialogContent>
            Are you sure you want to delete the field "{fieldToDelete?.id}"? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={confirmDeleteField} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {/* Import Fields Dialog */}
      <ModalPortal isOpen={importDialogOpen}>
        <ImportFieldsDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          definition={definition}
          currentUser={currentUser}
          onImport={(updatedDef) => {
            setImportDialogOpen(false);
            const fieldsArray = Object.entries(updatedDef.fields || {}).map(([key, data]) => ({
              id: key,
              ...data
            }));
            setFields(fieldsArray);
            onUpdate(updatedDef);
          }}
        />
      </ModalPortal>
    </div>
  );
};

// Dialog for creating/editing individual fields
const FieldDialog = ({ open, onClose, schema, editingField, existingKeys, onSave }) => {
  const [fieldKey, setFieldKey] = useState("");
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const safeSchema = schema || DEFAULT_SCHEMA;
  const schemaEntries = Object.entries(safeSchema);

  useEffect(() => {
    if (editingField) {
      setFieldKey(editingField.id || "");
      const vals = {};
      schemaEntries.forEach(([key]) => {
        vals[key] = editingField[key] || "";
      });
      setValues(vals);
    } else {
      setFieldKey("");
      const vals = {};
      schemaEntries.forEach(([key, config]) => {
        vals[key] = config.type === "boolean" ? false : "";
      });
      setValues(vals);
    }
    setError("");
  }, [editingField, open, schema]);

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setError("");
    
    if (!fieldKey.trim()) {
      setError("Key is required");
      return;
    }

    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(fieldKey.trim())) {
      setError("Key must start with a letter and contain only letters, numbers, and underscores");
      return;
    }

    // Check for duplicate key (only if creating new or changing key)
    if (!editingField && existingKeys.includes(fieldKey.trim())) {
      setError("A field with this key already exists");
      return;
    }

    // Validate required fields
    for (const [key, config] of schemaEntries) {
      if (config.required && !values[key]) {
        setError(`${key} is required`);
        return;
      }
    }

    setSaving(true);
    const fieldData = { id: fieldKey.trim(), ...values };
    onSave(fieldData, !editingField);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>{editingField ? "Edit Field" : "Add Field"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Key"
            value={fieldKey}
            onChange={(e) => setFieldKey(e.target.value)}
            fullWidth
            placeholder="e.g., blood_pressure"
            helperText="Unique identifier for this field"
            disabled={!!editingField}
          />
          {schemaEntries.map(([key, config]) => (
            <TextField
              key={key}
              label={key + (config.required ? " *" : "")}
              value={config.type === "boolean" ? undefined : values[key] || ""}
              onChange={(e) => handleChange(key, config.type === "number" ? Number(e.target.value) : e.target.value)}
              fullWidth
              type={config.type === "number" ? "number" : "text"}
              // For boolean, we'd need a checkbox, but for simplicity using text for now
            />
          ))}
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Dialog for importing fields into a definition
const ImportFieldsDialog = ({ open, onClose, definition, currentUser, onImport }) => {
  const [jsonText, setJsonText] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setJsonText("");
    setError("");
    setPreview(null);
  }, [open]);

  const handlePreview = () => {
    setError("");
    try {
      const data = JSON.parse(jsonText);
      if (typeof data !== 'object' || Array.isArray(data)) {
        setError("JSON must be an object with field keys");
        return;
      }
      
      const count = Object.keys(data).length;
      if (count === 0) {
        setError("No fields found in JSON");
        return;
      }
      
      setPreview({ data, count });
    } catch (e) {
      setError("Invalid JSON: " + e.message);
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      // Merge with existing fields
      const updatedFields = { ...definition.fields, ...preview.data };
      
      await updateDoc(doc(db, COLLECTION_NAME, definition.id), {
        fields: updatedFields,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      });
      
      onImport({ ...definition, fields: updatedFields });
    } catch (error) {
      console.error("Error importing:", error);
      setError("Import failed: " + error.message);
    }
    setImporting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>Import Fields</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            Paste a JSON object with field keys and values:
            <pre style={{ margin: '8px 0', padding: '8px', background: 'var(--color-background-alt)', borderRadius: '4px', overflow: 'auto' }}>
{`{
  "field_key": { "name": "Field Name" },
  "another_key": { "name": "Another Field" }
}`}
            </pre>
          </Box>
          <TextField
            label="JSON Object"
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setPreview(null); }}
            fullWidth
            multiline
            rows={10}
            sx={{ 
              '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 'var(--text-sm)' }
            }}
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
          {preview && (
            <Box sx={{ p: 2, bgcolor: 'var(--color-background-alt)', borderRadius: 1 }}>
              <strong>{preview.count} fields ready to import</strong>
              <Box sx={{ mt: 1, fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                Keys: {Object.keys(preview.data).slice(0, 5).join(", ")}
                {Object.keys(preview.data).length > 5 && ` ... and ${Object.keys(preview.data).length - 5} more`}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!preview ? (
          <Button onClick={handlePreview} variant="outlined">
            Preview
          </Button>
        ) : (
          <Button onClick={handleImport} variant="contained" disabled={importing}>
            {importing ? "Importing..." : `Import ${preview.count} Fields`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Dialog for importing full definitions
const ImportDefinitionsDialog = ({ open, onClose, currentUser, onImport }) => {
  const [jsonText, setJsonText] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setJsonText("");
    setError("");
    setPreview(null);
  }, [open]);

  const handlePreview = () => {
    setError("");
    try {
      const data = JSON.parse(jsonText);
      if (typeof data !== 'object' || Array.isArray(data)) {
        setError("JSON must be an object with definition IDs as keys");
        return;
      }
      
      const entries = Object.entries(data).map(([id, value]) => ({
        id,
        ...value
      }));
      
      if (entries.length === 0) {
        setError("No field definitions found in JSON");
        return;
      }
      
      setPreview(entries);
    } catch (e) {
      setError("Invalid JSON: " + e.message);
    }
  };

  const handleImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const promises = preview.map(item => {
        const { id, ...data } = item;
        return setDoc(doc(db, COLLECTION_NAME, id), {
          ...data,
          schema: data.schema || DEFAULT_SCHEMA,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid
        });
      });
      await Promise.all(promises);
      onImport();
    } catch (error) {
      console.error("Error importing:", error);
      setError("Import failed: " + error.message);
    }
    setImporting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>Import Field Definitions</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
            Paste the dataFields object. Format:
            <pre style={{ margin: '8px 0', padding: '8px', background: 'var(--color-background-alt)', borderRadius: '4px', overflow: 'auto' }}>
{`{
  "mentalStatusFields": {
    "displayName": "Mental Status Fields",
    "category": "fieldGroup",
    "schema": { "name": { "type": "string", "required": true } },
    "fields": { ... }
  }
}`}
            </pre>
          </Box>
          <TextField
            label="JSON Object"
            value={jsonText}
            onChange={(e) => { setJsonText(e.target.value); setPreview(null); }}
            fullWidth
            multiline
            rows={12}
            sx={{ 
              '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 'var(--text-sm)' }
            }}
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: 'var(--text-sm)' }}>{error}</Box>
          )}
          {preview && (
            <Box sx={{ p: 2, bgcolor: 'var(--color-background-alt)', borderRadius: 1 }}>
              <strong>{preview.length} definitions ready to import:</strong>
              <Box sx={{ mt: 1, fontSize: 'var(--text-sm)' }}>
                {preview.map(p => (
                  <div key={p.id}>
                    <code>{p.id}</code> - {p.displayName || "(no display name)"} ({p.category})
                  </div>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!preview ? (
          <Button onClick={handlePreview} variant="outlined">
            Preview
          </Button>
        ) : (
          <Button onClick={handleImport} variant="contained" disabled={importing}>
            {importing ? "Importing..." : `Import ${preview.length} Definitions`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Help documentation dialog
const HelpDialog = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth sx={{ zIndex: 'var(--z-modal-backdrop)' }}>
      <DialogTitle>Document Fields Documentation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          
          <Box>
            <h4 style={{ margin: '0 0 8px 0' }}>Overview</h4>
            <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
              Field definitions configure the data structure for document sections. There are two categories:
            </p>
          </Box>

          <Box>
            <h4 style={{ margin: '0 0 8px 0' }}>Field Groups</h4>
            <p style={{ margin: '0 0 8px 0', color: 'var(--color-text-secondary)' }}>
              Define named fields with display names and optional default values. Used for data sections like Mental Status Exam.
            </p>
          </Box>

          <Box>
            <h4 style={{ margin: '0 0 8px 0' }}>Table Columns</h4>
            <p style={{ margin: '0 0 8px 0', color: 'var(--color-text-secondary)' }}>
              Define columns for table-style sections like Medications. Each column has type and width.
            </p>
          </Box>

          <Box>
            <h4 style={{ margin: '0 0 8px 0' }}>Schema</h4>
            <p style={{ margin: '0 0 8px 0', color: 'var(--color-text-secondary)' }}>
              Each definition has a schema that defines what properties each field can have. 
              Access via the Settings icon. Example schema:
            </p>
            <pre style={{ 
              margin: 0, 
              padding: '12px', 
              background: 'var(--color-background-alt)', 
              borderRadius: '4px',
              fontSize: 'var(--text-xs)',
              overflow: 'auto'
            }}>
{`{
  "name": { "type": "string", "required": true },
  "defaultValue": { "type": "string", "required": false },
  "width": { "type": "number", "required": false }
}`}
            </pre>
          </Box>

          <Box sx={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', borderTop: '1px solid var(--color-border)', pt: 2 }}>
            Full documentation: <code>docs/admin-document-builder.md</code>
          </Box>

        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AdminFieldsEditor;
